<template>
  <div class="bg-white bs-low br-low p-3 el-table">
    <div class="d-flex align-items-center flex-gap-10 mb-3">
      <el-input size="medium" placeholder="Введите номер заказа или ФИО клиента" v-model="search">
        <i slot="prefix" class="el-input__icon el-icon-search" />
      </el-input>
      <el-button type="text" :class="[$style['filter-btn'], { [$style.filtered]: filtered }]" @click="showFilterDialog = true">
        <IconFilter class="mr-1" width="20" height="20"/>Фильтр
      </el-button>
      <el-button size="medium" @click="showExportReportDialog = true">
        <div class="d-flex align-items-center">Выгрузить в Excel<IconDownload class="ml-1" width="14" height="14"/></div>
      </el-button>
      <el-button v-if="$isAccess($userRoles.Admin, $userRoles.Dispatcher)"
                 size="medium" :loading="importExportProgress" class="d-flex" @click="showImportDialog = true">
        <div class="d-flex align-items-center">Импорт из Excel<IconUpload class="ml-1" width="14" height="14"/></div>
      </el-button>
      <el-button v-if="$isAccess($userRoles.Admin)" type="primary" size="medium" icon="el-icon-plus" @click="showCreateDialog = true">
        Добавить заказ
      </el-button>
    </div>
    <div class="d-flex align-items-center flex-gap-10 mb-3 font-">
      <el-checkbox size="medium" label="Цветовое выделение заказов" :value="orderTableHighlightColors" @change="setOrderTableHighlightColors" />

      <el-tooltip placement="right" effect="light" content="Настроить отображение списка заказов на услуги">
        <el-button type="text" :class="$style['filter-btn']" @click="showTableSettingsDialog = true">
          <i class="el-icon-s-tools el-icon-hover pointer" />
        </el-button>
      </el-tooltip>

      <el-button v-if="$isAccess($userRoles.Admin)"
                 class="ml-auto" size="medium" plain
                 :disabled="!selectedIds.length"
                 @click="showOrdersDeleteDialog=true">
        Удалить выделеные заказы
      </el-button>
    </div>
    <div>
      <el-table v-loading="fetchProgress" ref="table" :data="items" border :stripe="!orderTableHighlightColors"
                :row-class-name="tableRowClassName"
                @header-dragend="onResizeCol"
                @current-change="onChangeSelected"
                @sort-change="onChangeSort"
                @selection-change="onSelectionChange">

        <template v-for="col in columns">

          <el-table-column v-if="col.isCustomTemplate"
                           :key="col.prop"
                           :prop="col.prop"
                           :label="col.label"
                           :sortable="col.sortable"
                           :class-name="col.className"
                           :resizable="col.resizable"
                           :width="getColWidth(col)">

            <template v-if="col.prop === 'selection'" slot="header" slot-scope="{}">
              <el-checkbox :value="isAllSelected" @change="onChangeAllSelected" />
            </template>

            <template slot-scope="{row}">
              <template v-if="col.prop === 'selection'">
                <div @click.stop>
                  <el-checkbox :value="selectedIds.includes(row.id)" @change="onChangeItemSelected(row)"/>
                </div>
              </template>
              <template v-if="col.prop === 'claimData.isProcessed'">
                <span :class="['status status-circle', { status_success: row.claimData && row.claimData.isProcessed }]" />
              </template>
              <template v-else-if="col.prop === 'amountByService'">
                {{ row.orderRows ? getAmountByServiceValue(row.orderRows) : '' }}
              </template>
              <template v-else-if="col.prop === 'amountByGoods'">
                {{ row.orderRows ? getAmountByGoodsValue(row.orderRows) : '' }}
              </template>
              <template v-else-if="col.prop === 'orderRows'">
                <el-tooltip placement="right" effect="light">
                  <div slot="content" v-html="getOrderRows(row.orderRows)" />
                  <span v-html="getOrderRows(row.orderRows, 2)" />
                </el-tooltip>
              </template>
              <template v-else-if="col.prop === 'workDate'">
                {{ row.workDate ? $moment.utc(row.workDate).format('DD.MM.YYYY') : ''}}
              </template>
              <template v-else-if="col.prop === 'claimData.processedAt'">
                {{ row.claimData && row.claimData.processedAt ? $moment.utc(row.claimData.processedAt).format('DD.MM.YYYY') : '' }}
              </template>
              <template v-else-if="col.prop === 'claimData.createdAt'">
                {{ row.claimData && row.claimData.createdAt ? dateFormatToMoscowTime(row.claimData.createdAt) + '(МСК)' : '' }}
              </template>
              <template v-else-if="col.prop === 'service.name'">
                {{row.service ? row.service.displayName : ''}}
              </template>
              <template v-else-if="col.prop === 'registryType.name'">
                {{row.registryType ? row.registryType.displayName : ''}}
              </template>
            </template>
          </el-table-column>

          <el-table-column v-else
                           :key="col.prop"
                           :prop="col.prop"
                           :type="col.type"
                           :label="col.label"
                           :sortable="col.sortable"
                           :resizable="col.resizable"
                           :width="getColWidth(col)"/>

        </template>

      </el-table>

      <div class="d-flex justify-content-between mt-4">
        <div class="text-secondary">Всего {{ count }} {{ declOfNum(count, 'заказ', 'заказа', 'заказов') }}</div>
        <el-pagination
          @current-change="onChangePage"
          :current-page="page"
          :page-size="pageSize"
          :pager-count="5"
          layout="prev, pager, next, slot"
          :total="count">
          <el-select :value="pageSize" @change="setPageSize" size="mini" class="ml-3">
            <el-option v-for="size in [10, 20, 30, 50]" :key="size" :label="size" :value="size"/>
          </el-select>
        </el-pagination>
      </div>
    </div>

    <OrdersExportReportDialog v-if="showExportReportDialog" :visible.sync="showExportReportDialog" />
    <OrderCreateDialog v-if="showCreateDialog" :visible.sync="showCreateDialog" />
    <OrdersFilterDialog v-if="showFilterDialog" :visible.sync="showFilterDialog" :updateUrl="updateUrl" />
    <OrdersImportDialog v-if="showImportDialog" :visible.sync="showImportDialog" />
    <OrderTableSettingsDialog v-if="showTableSettingsDialog" :visible.sync="showTableSettingsDialog" />
    <OrdersDeleteDialog v-if="showOrdersDeleteDialog" :visible.sync="showOrdersDeleteDialog"
                        :items="mappedSelectedOrders"
                        @deleteId="deleteIdFromSelected"
                        @openConfirmation="openConfirmation"/>
    <OrdersDeleteDoubleConfirmationDialog v-if="showOrdersDeleteDoubleConfirmationDialog" :visible.sync="showOrdersDeleteDoubleConfirmationDialog"
                        :items="mappedSelectedOrders"
                        @deleteId="deleteIdFromSelected"/>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { dateFormatToMoscowTime, declOfNum, formatPrice } from '@/commons/helpers';
import IconFilter from '@/components/ui/icons/IconFilter';
import IconDownload from '@/components/ui/icons/IconDownload';
import IconUpload from '@/components/ui/icons/IconUpload';
import OrderCreateDialog from '@/components/orders/dialogs/OrderCreateDialog';
import OrdersExportReportDialog from '@/components/orders/dialogs/OrdersExportReportDialog';
import OrdersFilterDialog from '@/components/orders/dialogs/OrdersFilterDialog';
import OrdersImportDialog from '@/components/orders/dialogs/OrdersImportDialog';
import OrderTableSettingsDialog from '@/components/orders/dialogs/OrderTableSettingsDialog';
import OrdersDeleteDialog from '@/components/orders/dialogs/OrdersDeleteDialog';
import OrdersDeleteDoubleConfirmationDialog from '@/components/orders/dialogs/OrdersDeleteDoubleConfirmationDialog';
import { ORDER_TABLE_COLUMNS } from '@/commons/constants/ordersTableCols';
import { ORDER_STATUS_CODES } from '@/commons/constants/orderStatusCodes';

export default {
  components: {
    IconFilter,
    IconDownload,
    IconUpload,
    OrdersExportReportDialog,
    OrderCreateDialog,
    OrdersFilterDialog,
    OrdersImportDialog,
    OrderTableSettingsDialog,
    OrdersDeleteDialog,
    OrdersDeleteDoubleConfirmationDialog,
  },
  data: () => ({
    showExportReportDialog: false,
    showCreateDialog: false,
    showFilterDialog: false,
    showImportDialog: false,
    showTableSettingsDialog: false,
    showOrdersDeleteDialog: false,
    showOrdersDeleteDoubleConfirmationDialog: false,
    selectedIds: [],
  }),
  computed: {
    ...mapGetters({
      fetchProgress: 'orders/fetchProgress',
      items: 'orders/items',
      count: 'orders/count',
      item: 'orders/item',
      pageSize: 'orders/pageSize',
      page: 'orders/page',
      itemId: 'orders/itemId',
      filter: 'orders/filter',
      filtered: 'orders/filtered',
      importExportProgress: 'orders/importExportProgress',
      ordersTableColumnsHide: 'orders/ordersTableColumnsHide',
      ordersTableColWidth: 'orders/ordersTableColWidth',
      orderTableHighlightColors: 'orders/orderTableHighlightColors',
    }),
    columns() {
      return ORDER_TABLE_COLUMNS.filter((c) => {
        const hideSelectedCol = c.prop === 'selection' && !this.$isAccess(this.$userRoles.Admin);
        if (hideSelectedCol) return false;

        return !this.ordersTableColumnsHide.includes(c.prop);
      });
    },
    search: {
      get() {
        return this.filter.search;
      },
      set(value) {
        this.setFilterValue({ key: 'search', value });
        this.debouncedFetchItems();
        if (this.page !== 1) {
          this.setPage(1);
        }
      },
    },

    isAllSelected() {
      if (!this.selectedIds.length) return false;
      return this.selectedIds.length === this.items.length;
    },
    mappedSelectedOrders() {
      return this.items.filter((x) => this.selectedIds.includes(x.id));
    },
  },
  methods: {
    ...mapMutations({
      setPage: 'orders/setPage',
      clearState: 'orders/clearState',
      clearFilter: 'orders/clearFilter',
      setFilterValue: 'orders/setFilterValue',
    }),
    ...mapActions({
      fetchItem: 'orders/fetchItem',
      fetchItems: 'orders/fetchItems',
      setSort: 'orders/setSort',
      setPageSize: 'orders/setPageSize',
      setItemId: 'orders/setItemId',
      setOrdersTableColWidth: 'orders/setOrdersTableColWidth',
      setOrderTableHighlightColors: 'orders/setOrderTableHighlightColors',
    }),
    dateFormatToMoscowTime,
    declOfNum,
    formatPrice,
    getAmountByServiceValue(orderRows) {
      if (orderRows.every((r) => r.servicePrice === null)) {
        return null;
      }
      return formatPrice(orderRows.reduce((acc, r) => acc + r.servicePrice, 0));
    },
    getAmountByGoodsValue(orderRows) {
      if (orderRows.every((r) => r.goodsPrice === null)) {
        return null;
      }
      return formatPrice(orderRows.reduce((acc, r) => acc + r.goodsPrice, 0));
    },
    getColWidth(col) {
      return this.ordersTableColWidth[col.prop] || col.width;
    },
    onResizeCol(newVal, oldVal, col) {
      this.setOrdersTableColWidth({ colName: col.property, value: newVal });
    },
    tableRowClassName({ row: order }) {
      if (!this.orderTableHighlightColors) {
        return order.id == this.item?.id ? 'current-row' : '';
      }

      let classRow = order.id == this.item?.id ? 'current-row ' : '';

      switch (order?.status?.code) {
        case ORDER_STATUS_CODES.COMPLETED: classRow += 'green-row'; break;
        case ORDER_STATUS_CODES.CLAIM: classRow += 'yellow-row'; break;
        case ORDER_STATUS_CODES.CANCELLED: classRow += 'red-row'; break;
        case ORDER_STATUS_CODES.WASTED_VISIT: classRow += 'purple-row'; break;
        default:
      }
      return classRow;
    },
    updateUrl() {
      const route = this.$route;
      const query = {
        ...route.query,
        id: this.itemId,
        ...this.filter,
      };
      // удаляю пустые ключи
      Object.keys(query).forEach((key) => (!query[key] && typeof query[key] !== 'boolean') && delete query[key]);
      this.$router.push({ ...route, query }).catch(() => {});
    },

    onChangeSelected(item) {
      if (item) {
        this.setItemId(item.id);
        this.updateUrl();
      }
    },

    onChangeSort({ prop, order }) {
      const propType = {
        number: 'number',
        'contractor.name': 'contractorName',
        workDate: 'workDate',
        shop: 'shop',
        clientName: 'clientName',
        address: 'address',
        'status.title': 'status',
        'claimData.processedAt': 'claimProcessedAt',
        'claimData.createdAt': 'claimCreatedAt',
        'claimData.isProcessed': 'claimProcessedStatus',
        'cancellationData.reason': 'cancellationReasonText',
        'service.name': 'serviceName',
        'registryType.name': 'registryType',
      };
      const sortType = propType[prop] || prop;
      const sortDirection = order === 'ascending' ? 'Asc' : order === 'descending' ? 'Desc' : null;
      this.setSort({ sortType, sortDirection });
    },

    onChangePage(page) {
      this.setPage(page);
      this.fetchItems();
      this.updateUrl();
    },

    // eslint-disable-next-line func-names
    debouncedFetchItems: debounce(function () {
      this.setPage(1);
      this.updateUrl();
      this.fetchItems();
    }, 500),

    getOrderRows(rows, max = rows.length) {
      const tmpRows = {};
      rows.forEach((r) => {
        tmpRows[r.orderNumber] = r;
      });
      return Object.values(tmpRows).filter((or, ind) => ind < max).map((or) => or.orderNumber).reduce((acc, text) => `${acc}${text}; <br>`, '');
    },

    clearSelected() {
      this.selectedIds = [];
    },

    deleteIdFromSelected(dId) {
      this.selectedIds = this.selectedIds.filter((id) => id !== dId);
    },

    openConfirmation() {
      this.showOrdersDeleteDialog = false;
      setTimeout(() => {
        this.showOrdersDeleteDoubleConfirmationDialog = true;
      }, 300);
    },

    onChangeAllSelected(bool) {
      if (bool) {
        this.selectedIds = this.items.map((x) => x.id);
      } else {
        this.clearSelected();
      }
    },
    onChangeItemSelected(item) {
      if (this.selectedIds.find((id) => id === item.id)) {
        this.deleteIdFromSelected(item.id);
      } else {
        this.selectedIds.push(item.id);
      }
    },

    onSelectionChange(data) {
      this.selectedIds = data.map((l) => l.id);
    },
  },
  created() {
    const { id } = this.$route.query;
    if (id) {
      this.setItemId(this.$route.query.id);
      this.fetchItem();
    }
    for (const key in this.filter) {
      if (this.$route.query[key]) {
        this.setFilterValue({ key, value: this.$route.query[key] });
      }
    }
    this.fetchItems();
  },
  destroyed() {
    this.clearState();
  },
  watch: {
    $route: {
      handler(newVal, oldVal) {
        if (newVal.query.id != oldVal.query.id) {
          this.setItemId(newVal.query.id);
          this.fetchItem().then(() => {
            this.$nextTick(() => {
              // eslint-disable-next-line no-unused-expressions
              document.getElementById('orderBlock')?.scrollIntoView({ behavior: 'smooth' });
            });
          });
        }
        if (!Object.keys(newVal.query).length) {
          this.clearFilter();
          this.fetchItems();
        }
      },
      deep: true,
    },
    items: {
      handler() {
        this.clearSelected();
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss" module>
@import "src/assets/styles/variables";
.filter-btn {
  font-size: 16px;
  color: $color-secondary;
  padding: 0;
  & span {
    position: relative;
    display: flex;
    align-items: center;
  }
  &.filtered span {
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 16px;
      width: 6px;
      height: 6px;
      background-color: $color-primary;
      border-radius: 50%;
    }
  }
  &:hover {
    color: $color-primary;
  }
}
</style>
